import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Mail, Phone } from "lucide-react";

const CONSULTING_CARDS = [
  {
    title: "Workforce and financial empowerment programs",
    description:
      "Custom programs that help employees and residents build financial stability, advance into higher-paying roles, and stay. Built on our Rules of Money curriculum, with career pathways, mental wellbeing support, and the Wealth Builders Club.",
    forWhom: "Employers, health systems, government entities, and nonprofits",
  },
  {
    title: "Leadership and operations consulting",
    description:
      "We help leaders get clear on what deserves their attention, delegate with confidence, and build the systems that keep the organization running without them carrying everything.",
    forWhom: "CEOs, owners, and nonprofit leaders",
  },
  {
    title: "Program and curriculum design",
    description:
      "We design, document, and launch programs your organization can run and grow, from cohort models to facilitator guides and outcome tracking.",
    forWhom: "Organizations launching or scaling community and workforce programs",
  },
];

const SPEAKING_TOPICS = [
  "From Surviving to Thriving: Building Financial Stability in the Workforce",
  "The Rules of Money: Simple Principles That Change Families",
  "Scattered to Systemized: Leading Without Carrying Everything",
  "Building Generational Wealth: From Stability to Legacy",
  "AI for Leaders: What to Use, What to Skip, and Where to Start",
];

const TRAINING_CARDS = [
  {
    title: "Rules of Money cohorts",
    description:
      "A 12-session financial empowerment program delivered to employees or community members, with our Money Rules software included for every participant.",
  },
  {
    title: "Facilitator certification (train-the-trainer)",
    description:
      "We certify your staff to deliver our programs, with facilitator guides, materials, and coaching support, so the work continues long after we leave.",
  },
  {
    title: "Wealth Builders Club",
    description:
      "An advanced program for growing savings, investing, homeownership, and building generational wealth.",
  },
  {
    title: "Leadership and team workshops",
    description:
      "Interactive sessions on communication, accountability, delegation, and culture.",
  },
];

function SectionHeading({ overline, title, intro }) {
  return (
    <div className="max-w-3xl mb-16">
      <p className="tmg-overline mb-5">{overline}</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
        {title}
      </h2>
      <p className="mt-6 text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed">
        {intro}
      </p>
    </div>
  );
}

export default function Offerings() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    }
  }, [location.hash]);

  return (
    <>
      {/* INTRO */}
      <section
        data-testid="offerings-hero"
        className="relative bg-[var(--tmg-ivory)] border-b border-[var(--tmg-line)]"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <p className="tmg-overline mb-6 tmg-fade-up">— Offerings</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight max-w-4xl tmg-fade-up-d1">
            Our offerings
          </h1>
          <p className="mt-8 text-base sm:text-lg text-[var(--tmg-muted)] max-w-2xl leading-relaxed tmg-fade-up-d2">
            Four ways we help organizations, leaders, and growing businesses build
            stronger people and systems that last.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 tmg-fade-up-d3">
            {[
              ["Consulting", "#consulting"],
              ["Speaking", "#speaking"],
              ["Training", "#training"],
              ["AI", "#ai"],
            ].map(([label, href]) => (
              <a
                key={href}
                href={href}
                data-testid={`jump-${label.toLowerCase()}`}
                className="tmg-btn-secondary"
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 1. CONSULTING */}
      <section id="consulting" data-testid="section-consulting" className="tmg-section scroll-mt-24">
        <div className="tmg-container">
          <SectionHeading
            overline="— 01 · Consulting"
            title="Consulting"
            intro="Strategic guidance and custom programs for organizations ready to invest in their people and operations."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--tmg-line)] mb-12">
            {CONSULTING_CARDS.map((c, i) => (
              <div
                key={c.title}
                data-testid={`consulting-card-${i}`}
                className="bg-white p-8 sm:p-10 hover:bg-[var(--tmg-ivory)] transition-all duration-300 flex flex-col"
              >
                <h3 className="font-display text-2xl font-semibold mb-4 leading-tight">
                  {c.title}
                </h3>
                <p className="text-sm text-[var(--tmg-muted)] leading-relaxed mb-8 flex-1">
                  {c.description}
                </p>
                <p className="font-mono-sm text-[10px] uppercase tracking-[0.18em] text-[var(--tmg-gold-soft)]">
                  For: {c.forWhom}
                </p>
              </div>
            ))}
          </div>
          <Link to="/book-call" data-testid="consulting-cta-btn" className="tmg-btn-primary">
            Book a discovery call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* 2. SPEAKING */}
      <section
        id="speaking"
        data-testid="section-speaking"
        className="tmg-section bg-[var(--tmg-ivory-2)]/40 border-y border-[var(--tmg-line)] scroll-mt-24"
      >
        <div className="tmg-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="tmg-overline mb-5">— 02 · Speaking</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-6">
              Speaking
            </h2>
            <p className="text-base sm:text-lg text-[var(--tmg-muted)] leading-relaxed mb-6">
              Practical, energizing talks that leave audiences with clarity and a
              next step.
            </p>
            <p className="font-mono-sm text-[10px] uppercase tracking-[0.18em] text-[var(--tmg-gold-soft)]">
              Keynotes · Workshops · Panels · Conferences and staff development days
            </p>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-[var(--tmg-line)]">
            <p className="tmg-overline mb-6">— Signature Topics</p>
            <ul className="space-y-3 mb-10">
              {SPEAKING_TOPICS.map((t, i) => (
                <li
                  key={t}
                  data-testid={`speaking-topic-${i}`}
                  className="flex items-center gap-4 border-b border-[var(--tmg-line)] py-4"
                >
                  <span className="font-mono-sm text-xs text-[var(--tmg-gold)] w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-lg sm:text-xl leading-snug">{t}</span>
                </li>
              ))}
            </ul>
            <p className="text-base text-[var(--tmg-muted)] leading-relaxed mb-10">
              Talks can be tailored to your audience, including employers,
              nonprofits, community organizations, faith-based groups, and
              business associations.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              <Link to="/book-call" data-testid="speaking-cta-btn" className="tmg-btn-primary">
                Check speaking availability <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href="/programs/speaking-one-sheet.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-speaker-one-sheet"
                className="inline-flex items-center gap-2 text-xs font-semibold font-mono-sm uppercase tracking-[0.14em] text-[var(--tmg-gold-soft)] border-b border-[var(--tmg-gold)] pb-1 hover:text-[var(--tmg-obsidian)] transition-colors"
              >
                Download speaker one-sheet <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3. TRAINING */}
      <section id="training" data-testid="section-training" className="tmg-section scroll-mt-24">
        <div className="tmg-container">
          <SectionHeading
            overline="— 03 · Training"
            title="Training"
            intro="Hands-on training and certification that builds lasting capacity inside your organization."
          />
          <div className="grid md:grid-cols-2 gap-px bg-[var(--tmg-line)] mb-12">
            {TRAINING_CARDS.map((c, i) => (
              <div
                key={c.title}
                data-testid={`training-card-${i}`}
                className="bg-white p-8 sm:p-10 hover:bg-[var(--tmg-ivory)] transition-all duration-300"
              >
                <h3 className="font-display text-2xl font-semibold mb-4 leading-tight">
                  {c.title}
                </h3>
                <p className="text-sm text-[var(--tmg-muted)] leading-relaxed">
                  {c.description}
                </p>
              </div>
            ))}
          </div>
          <Link to="/book-call" data-testid="training-cta-btn" className="tmg-btn-primary">
            Bring a training to your team <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* 4. AI */}
      <section
        id="ai"
        data-testid="section-ai"
        className="tmg-section bg-[var(--tmg-ivory-2)]/40 border-y border-[var(--tmg-line)] scroll-mt-24"
      >
        <div className="tmg-container">
          <SectionHeading
            overline="— 04 · AI"
            title="AI"
            intro="Practical AI education that helps leaders and teams work smarter, without the hype."
          />
          <div className="grid md:grid-cols-2 gap-px bg-[var(--tmg-line)] mb-12">
            <div
              data-testid="ai-card-literacy"
              className="bg-white p-8 sm:p-10 hover:bg-[var(--tmg-ivory)] transition-all duration-300"
            >
              <h3 className="font-display text-2xl font-semibold mb-4 leading-tight">
                AI literacy for leaders and teams
              </h3>
              <p className="text-sm text-[var(--tmg-muted)] leading-relaxed">
                Workshops that show your people how to use AI safely and
                effectively in their everyday work.
              </p>
            </div>
            <div
              data-testid="ai-card-checklist"
              className="bg-white p-8 sm:p-10 hover:bg-[var(--tmg-ivory)] transition-all duration-300"
            >
              <h3 className="font-display text-2xl font-semibold mb-4 leading-tight">
                AI readiness checklist{" "}
                <span className="bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] text-[10px] font-mono-sm font-bold uppercase tracking-wider px-2 py-1 align-middle">
                  Free
                </span>
              </h3>
              <p className="text-sm text-[var(--tmg-muted)] leading-relaxed mb-8">
                A quick self-check that shows how ready your business is to use
                AI and what to put in place first.
              </p>
              <Link
                to="/ai-readiness"
                data-testid="ai-checklist-btn"
                className="inline-flex items-center gap-2 text-sm font-semibold border-b border-[var(--tmg-obsidian)] pb-1 hover:border-[var(--tmg-gold)] hover:text-[var(--tmg-gold-soft)] transition-colors"
              >
                Take the free checklist <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="border-l-4 border-[var(--tmg-gold)] bg-white p-8 sm:p-10 max-w-4xl">
            <p className="text-base text-[var(--tmg-muted)] leading-relaxed">
              Ready to implement? Our technology partner, Opturna, builds AI
              systems and automation and offers the full AI &amp; Business
              Systems Audit.
            </p>
          </div>
        </div>
      </section>

      {/* RESULTS STRIP */}
      <section data-testid="results-strip" className="tmg-section">
        <div className="tmg-container max-w-4xl">
          <div className="border-l-4 border-[var(--tmg-gold)] bg-[var(--tmg-ivory-2)]/60 p-8 sm:p-10 flex items-start gap-6">
            <span className="font-display text-5xl sm:text-6xl font-semibold text-[var(--tmg-gold)] leading-none">
              89%
            </span>
            <p className="text-base sm:text-lg text-[var(--tmg-obsidian)] leading-relaxed">
              89% participant retention in a community program led by our
              principal consultant, with every participant showing improvement
              in their living situation.
            </p>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section
        data-testid="offerings-closing-cta"
        className="relative overflow-hidden bg-[var(--tmg-obsidian)] text-[var(--tmg-ivory)] tmg-grain"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 20%, rgba(212,175,55,0.18), transparent 50%), radial-gradient(circle at 20% 80%, rgba(212,175,55,0.10), transparent 50%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <p className="tmg-overline-gold mb-6">— The Next Step</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Not sure where to start?
            </h2>
            <p className="mt-8 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">
              Tell us what you're working toward. In a 30-minute discovery call,
              we'll recommend the right offering, or tell you honestly if we're
              not the right fit.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/70">
              <a
                href="mailto:info@tmariegreene.com"
                data-testid="closing-email-link"
                className="inline-flex items-center gap-2 hover:text-[var(--tmg-gold)] transition-colors"
              >
                <Mail className="w-4 h-4" /> info@tmariegreene.com
              </a>
              <a
                href="tel:+13137431904"
                data-testid="closing-phone-link"
                className="inline-flex items-center gap-2 hover:text-[var(--tmg-gold)] transition-colors"
              >
                <Phone className="w-4 h-4" /> (313) 743-1904
              </a>
            </div>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/book-call"
              data-testid="closing-book-call-btn"
              className="tmg-btn-primary"
            >
              Book a discovery call <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
